import React, { useTransition } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Stack } from '@startuptools/ui/Stack';
import { FormControl } from '@startuptools/ui/FormControl';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateInput } from '@startuptools/ui/DateInput';
import { DateTime } from 'luxon';
import { type GqlWarrantParticipant, type GqlWarrantProgram } from '../../../../../../graphql/base-types.graphql';
import { useVestedOnDateSuspenseQuery } from './end-vesting.graphql';
import { Switch } from '@startuptools/ui/Switch';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';

type FormData = { vestingEndDate: DateTime | null; quantityInvalidated: string | null };

export const EndVesting = ({
  error,
  closeDialog,
  onSubmit,
  participant,
  wpp,
  loading,
}: {
  error?: React.ReactNode | undefined;
  closeDialog: () => void;
  onSubmit: SubmitHandler<FormData>;
  wpp: Pick<GqlWarrantProgram, 'warrantStartDate'>;
  participant: Pick<
    GqlWarrantParticipant,
    'vestingLastPotentialDate' | 'id' | 'vestingEndDate' | 'quantityInvalidated'
  >;
  loading: boolean;
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      vestingEndDate: participant.vestingEndDate ? DateTime.fromISO(participant.vestingEndDate) : null,
      quantityInvalidated: participant.quantityInvalidated ?? null,
    },
  });
  const [_isPending, startTransition] = useTransition();
  const [localDate, setLocalDate] = React.useState<DateTime | null>(
    participant.vestingEndDate ? DateTime.fromISO(participant.vestingEndDate) : null,
  );

  const { data } = useVestedOnDateSuspenseQuery({
    variables: {
      wppId: participant.id,
      date: localDate?.toUTC(0, { keepLocalTime: true }).toISODate(),
    },
    skip: !localDate,
    fetchPolicy: 'network-only',
  });

  const { i18n } = useLingui();

  return (
    <>
      <Typography>
        <Trans id="End vesting" />
      </Typography>
      <form
        onSubmit={form.handleSubmit(async data => {
          await onSubmit({
            ...data,
            quantityInvalidated: data.quantityInvalidated?.length ? data.quantityInvalidated : null,
          });
          closeDialog();
        })}
      >
        <Box sx={{ width: '420px', overflowY: 'auto' }}>
          <Stack spacing={3}>
            <Alert>
              <Trans id="Set the date after which no further vesting will occur. The subscriber will still be able to excercise already vested options. " />
            </Alert>

            <Controller
              control={form.control}
              name="vestingEndDate"
              render={({ field: { onChange, ...field } }) => (
                <>
                  <Switch
                    checked={Boolean(field.value)}
                    sx={{ alignSelf: 'flex-start' }}
                    onChange={event => {
                      if (!event.target.checked) {
                        onChange(null);
                        startTransition(() => {
                          setLocalDate(null);
                        });
                      } else {
                        startTransition(() => {
                          setLocalDate(DateTime.now().startOf('day'));
                        });
                        onChange(DateTime.now().startOf('day'));
                      }
                    }}
                    endDecorator={<Trans id="End vesting" />}
                  />
                  {Boolean(field.value) && (
                    <FormControl>
                      <FormLabel>
                        <Trans id="Vesting end date" />
                      </FormLabel>
                      <DateInput
                        {...field}
                        minDate={wpp.warrantStartDate ? DateTime.fromISO(wpp.warrantStartDate) : undefined}
                        maxDate={
                          participant?.vestingLastPotentialDate
                            ? DateTime.fromISO(participant.vestingLastPotentialDate)
                            : undefined
                        }
                        slotProps={{ field: { clearable: true } }}
                        onChange={newValue => {
                          onChange(newValue);
                          startTransition(() => {
                            setLocalDate(newValue);
                          });
                        }}
                      />
                    </FormControl>
                  )}
                </>
              )}
            />
            {localDate && (
              <Alert>
                {data.warrantParticipant.hasDatelessVesting ? (
                  <Trans
                    id="At most {quantity} options will have vested on this date. One or more of the vesting occurences have a requirement but no date, these need to be handled manually."
                    values={{
                      quantity: (
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {data.warrantParticipant.vestedQuantity}
                        </NumberFormat>
                      ),
                    }}
                  />
                ) : (
                  <Trans
                    id="{quantity} options will have vested on this date."
                    values={{
                      quantity: (
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0} roundingMode="floor">
                          {data.warrantParticipant.vestedQuantity}
                        </NumberFormat>
                      ),
                    }}
                  />
                )}
              </Alert>
            )}

            {form.watch('vestingEndDate') && (
              <Controller
                control={form.control}
                name="quantityInvalidated"
                rules={{ min: 0 }}
                render={({ field }) => (
                  <FormControl
                    error={!!form.formState.errors.quantityInvalidated}
                    sx={{
                      display: 'inline-flex',
                      mx: '4px',
                    }}
                  >
                    <FormLabel>
                      <Trans id="Invalidated options" />
                    </FormLabel>
                    <FormHelperText>
                      <Trans id="Set this if certain options cannot be subscribed to for any reason. Note that subscribers generally retain the right to their vested options, even if the vesting period ends prematurely." />
                    </FormHelperText>
                    <NumericFormatInput
                      value={field.value ?? ''}
                      onChange={value => field.onChange(value)}
                      decimalScale={0}
                    />
                  </FormControl>
                )}
              />
            )}

            {error}
            <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
              <Button variant="plain" onClick={closeDialog} color="neutral">
                <Trans id="Cancel" />
              </Button>
              <Button type="submit" variant="solid" color="primary" loading={loading}>
                <Trans id="Save" />
              </Button>
            </Stack>
          </Stack>
        </Box>
      </form>
    </>
  );
};
